import React, { useState, useCallback, useRef, useEffect } from "react";
import { useCentralStore } from "../../../store/Central";
import { Loader2, Plus, Trash2, Star, StarOff, FileInput } from "lucide-react";
import { PriceList } from "../../../types/price-list.types";
import { REGION_OPTIONS } from "../../../types/region.types";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Switch } from "../../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../../../components/ui/table";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { PriceListImportDialog } from "./components/price-list-import-dialog.component";

export const PreislistenPage: React.FC = () => {
	const {
		priceLists,
		priceListsLoading,
		organization,
		activatedRegions,
		createPriceList,
		updatePriceListState,
		updatePriceListDb,
		deletePriceList,
		setDefaultPriceList,
	} = useCentralStore((state) => ({
		priceLists: state.priceLists,
		priceListsLoading: state.priceListsLoading,
		organization: state.organization,
		activatedRegions: state.activatedRegions,
		createPriceList: state.createPriceList,
		updatePriceListState: state.updatePriceListState,
		updatePriceListDb: state.updatePriceListDb,
		deletePriceList: state.deletePriceList,
		setDefaultPriceList: state.setDefaultPriceList,
	}));

	const [newPriceListName, setNewPriceListName] = useState("");
	const [selectedRegionId, setSelectedRegionId] = useState<string>("");
	const [isAdding, setIsAdding] = useState(false);
	const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
	const [importDialogOpen, setImportDialogOpen] = useState(false);
	const debounceTimers = useRef<{ [key: string]: NodeJS.Timeout }>({});

	const handleAddPriceList = async () => {
		if (!newPriceListName.trim()) return;

		const regionId = selectedRegionId ? parseInt(selectedRegionId) : undefined;
		setIsAdding(true);
		await createPriceList(newPriceListName.trim(), regionId);
		setNewPriceListName("");
		setSelectedRegionId("");
		setIsAdding(false);
	};

	const handleUpdateField = useCallback((id: string, field: keyof PriceList, value: any) => {
		// Update state immediately for responsive UI
		updatePriceListState(id, field, value);

		// Clear existing timer for this field
		const timerKey = `${id}-${field}`;
		if (debounceTimers.current[timerKey]) {
			clearTimeout(debounceTimers.current[timerKey]);
		}

		// For boolean fields, save to DB immediately
		if (field === 'is_deactivated') {
			updatePriceListDb(id, field, value);
			return;
		}

		// For tp_value and text/date fields, debounce DB update
		debounceTimers.current[timerKey] = setTimeout(() => {
			updatePriceListDb(id, field, value || null);
			delete debounceTimers.current[timerKey];
		}, 500);
	}, [updatePriceListState, updatePriceListDb]);

	const handleDelete = async (id: string) => {
		await deletePriceList(id);
		setDeleteConfirmId(null);
	};

	const handleSetDefault = async (id: string) => {
		const defaultPriceListId = (organization as any)?.default_price_list_id;
		if (defaultPriceListId === id) {
			// If already default, unset it
			await setDefaultPriceList(null);
		} else {
			await setDefaultPriceList(id);
		}
	};

	// Cleanup timers on unmount
	useEffect(() => {
		return () => {
			Object.values(debounceTimers.current).forEach(timer => clearTimeout(timer));
		};
	}, []);

	if (priceListsLoading) {
		return (
			<div className="p-6">
				<div className="max-w-6xl">
					<h1 className="text-2xl font-semibold text-gray-900 mb-6">
						Preislisten
					</h1>
					<div className="flex items-center justify-center py-12">
						<Loader2 className="h-8 w-8 animate-spin text-gray-500" />
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="p-6">
			<div className="max-w-6xl">
				<h1 className="text-2xl font-semibold text-gray-900 mb-6">Preislisten</h1>

				{/* Add new price list */}
				<div className="mb-6 flex gap-2">
					<Input
						placeholder="Name der neuen Preisliste"
						value={newPriceListName}
						onChange={(e) => setNewPriceListName(e.target.value)}
						onKeyPress={(e) => {
							if (e.key === 'Enter') {
								handleAddPriceList();
							}
						}}
						className="max-w-sm"
						disabled={isAdding}
					/>
					{activatedRegions && activatedRegions.length > 1 && (
						<Select value={selectedRegionId} onValueChange={setSelectedRegionId}>
							<SelectTrigger className="w-48">
								<SelectValue placeholder="Region wählen..." />
							</SelectTrigger>
							<SelectContent>
								{activatedRegions.map((region) => {
									const regionInfo = REGION_OPTIONS.find(r => r.value === region.region);
									return (
										<SelectItem key={region.region} value={region.region.toString()}>
											<span>{regionInfo?.label}</span>
										</SelectItem>
									);
								})}
							</SelectContent>
						</Select>
					)}
					<Button
						onClick={handleAddPriceList}
						disabled={!newPriceListName.trim() || isAdding || (activatedRegions && activatedRegions.length > 1 && !selectedRegionId)}
					>
						{isAdding ? (
							<Loader2 className="h-4 w-4 animate-spin mr-2" />
						) : (
							<Plus className="h-4 w-4 mr-2" />
						)}
						Hinzufügen
					</Button>
					<Button
						onClick={() => setImportDialogOpen(true)}
						variant="outline"
					>
						<FileInput className="h-4 w-4 mr-2" />
						Importieren
					</Button>
				</div>

				{/* Price lists table */}
				{priceLists.length === 0 ? (
					<div className="text-center py-12 text-gray-500">
						Keine Preislisten vorhanden. Fügen Sie eine neue Preisliste hinzu.
					</div>
				) : (
					<div className="border rounded-lg">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead className="w-8"></TableHead>
									<TableHead>Name</TableHead>
									<TableHead className="w-32">Region</TableHead>
									<TableHead className="w-36">Standard-TP-Wert</TableHead>
									<TableHead className="w-32">Start</TableHead>
									<TableHead className="w-32">Ende</TableHead>
									<TableHead className="w-20">Status</TableHead>
									<TableHead className="w-16"></TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{priceLists.map((priceList) => (
									<TableRow key={priceList.id}>
										<TableCell>
											<button
												onClick={() => handleSetDefault(priceList.id)}
												className="p-1 hover:bg-gray-100 rounded"
												title={
													(organization as any)?.default_price_list_id === priceList.id
														? "Als Standard entfernen"
														: "Als Standard setzen"
												}
											>
												{(organization as any)?.default_price_list_id === priceList.id ? (
													<Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
												) : (
													<StarOff className="h-4 w-4 text-gray-400" />
												)}
											</button>
										</TableCell>
										<TableCell>
											<Input
												value={priceList.name}
												onChange={(e) => handleUpdateField(priceList.id, 'name', e.target.value)}
												className="w-full"
											/>
										</TableCell>
										<TableCell>
											{(() => {
												const regionInfo = REGION_OPTIONS.find(r => r.value === priceList.region);
												return (
													<span className="text-sm">{regionInfo?.label}</span>
												);
											})()}
										</TableCell>
										<TableCell>
											<Input
												type="number"
												step="0.01"
												value={priceList.tp_value}
												onChange={(e) => handleUpdateField(priceList.id, 'tp_value', parseFloat(e.target.value) || 1)}
												className="w-full"
											/>
										</TableCell>
										<TableCell>
											<Input
												type="date"
												value={priceList.start_date || ''}
												onChange={(e) => handleUpdateField(priceList.id, 'start_date', e.target.value)}
												className="w-full"
											/>
										</TableCell>
										<TableCell>
											<Input
												type="date"
												value={priceList.end_date || ''}
												onChange={(e) => handleUpdateField(priceList.id, 'end_date', e.target.value)}
												className="w-full"
											/>
										</TableCell>
										<TableCell>
											<Switch
												checked={!priceList.is_deactivated}
												onCheckedChange={(checked) => handleUpdateField(priceList.id, 'is_deactivated', !checked)}
											/>
										</TableCell>
										<TableCell>
											<button
												onClick={() => setDeleteConfirmId(priceList.id)}
												className="p-1 hover:bg-gray-100 rounded text-red-600"
												title="Löschen"
												disabled={(organization as any)?.default_price_list_id === priceList.id}
											>
												<Trash2 className="h-4 w-4" />
											</button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				)}

				{/* Delete confirmation dialog */}
				<AlertDialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>Preisliste löschen?</AlertDialogTitle>
							<AlertDialogDescription>
								Sind Sie sicher, dass Sie diese Preisliste löschen möchten?
								Diese Aktion kann nicht rückgängig gemacht werden.
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel>Abbrechen</AlertDialogCancel>
							<AlertDialogAction
								onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
								className="bg-red-600 hover:bg-red-700"
							>
								Löschen
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>

				{/* Import dialog */}
				<PriceListImportDialog
					open={importDialogOpen}
					onOpenChange={setImportDialogOpen}
				/>
			</div>
		</div>
	);
};